import { Alert, Button, CircularProgress, IconButton } from '@mui/material';
import { useNavigate } from '@tanstack/react-router';
import { MdClose, MdOpenInNew } from '@teamworksdev/icons';
import { useTranslation } from 'react-i18next';
import {
  Drawer,
  DrawerFooter,
  DrawerHeader,
} from '../../../../components/drawer/drawer';
import { SingleWorkoutSheet } from '../../../block/block-all/SingleWorkoutSheet';
import { usePlanContext } from '../../context/PlanContext';
import { useSingleWorkout } from '../hooks/useSingleWorkout';

export type WorkoutPreviewDrawerProps = {
  open: boolean;
  blockId: string | null;
  workoutId: string | null;
  profileId: string | null;
  /**
   * Set when previewing a workout from one of the athlete's additional teams
   * ("Show Additional Teams"). The server scopes the block lookup to this
   * team; the "open in block" action is hidden since the block lives outside
   * the active team's context.
   */
  sourceTeamId?: string | null;
  onClose: () => void;
};

/**
 * Read-only "View Workout" drawer for the Annual Planner Calendar (SC-4020).
 * Fetches the raw published layers via `block.getWorkoutPreview`, merges them
 * client-side (the server contract intentionally returns unmerged layers —
 * see SC-4019), and renders the merged workout through the existing block
 * `SingleWorkoutSheet` in `forceReadOnly`.
 */
export function WorkoutPreviewDrawer({
  open,
  blockId,
  workoutId,
  profileId,
  sourceTeamId,
  onClose,
}: WorkoutPreviewDrawerProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { teamId } = usePlanContext();

  const { workout, editContext, isLoading } = useSingleWorkout({
    blockId,
    workoutId,
    profileId,
    sourceTeamId,
  });

  // Defensive fallback: the main query can resolve successfully with no
  // usable workout/editContext (e.g. an org-scoped access gap), which would
  // otherwise render a blank drawer body. `isError` is intentionally not
  // part of this condition: React Query keeps previously-fetched `data`
  // around on a failed background refetch (e.g. a window-focus refetch), so
  // a genuine first-load error already implies `workout`/`editContext` are
  // still null and is already covered below.
  const showUnavailable = !isLoading && (!workout || !editContext);

  const handleOpenInNew = () => {
    if (!blockId || !workoutId) return;
    navigate({
      to: '/$teamId/block/$blockId',
      params: { teamId, blockId },
      search: { workoutId, ...(profileId ? { athleteId: profileId } : {}) },
    });
    onClose();
  };

  return (
    <Drawer open={open} onClose={onClose}>
      <DrawerHeader>
        <div className="flex w-full items-center justify-between gap-2">
          <h2 className="m-0 text-xl font-semibold">{t('viewWorkout')}</h2>
          <div className="flex items-center">
            {!sourceTeamId && (
              <IconButton
                aria-label={t('openWorkoutInBlock')}
                onClick={handleOpenInNew}
              >
                <MdOpenInNew size={20} />
              </IconButton>
            )}
            <IconButton aria-label={t('close')} onClick={onClose}>
              <MdClose size={20} />
            </IconButton>
          </div>
        </div>
      </DrawerHeader>
      <section
        role="region"
        aria-label={t('viewWorkout')}
        className="min-h-0 flex-1 overflow-auto px-6 pb-6 pt-4"
      >
        {isLoading ? (
          <div className="flex h-full items-center justify-center">
            <CircularProgress />
          </div>
        ) : showUnavailable ? (
          <Alert severity="warning" role="status">
            {t('workoutPreviewUnavailable')}
          </Alert>
        ) : (
          workout &&
          editContext && (
            <SingleWorkoutSheet workout={workout} editContext={editContext} />
          )
        )}
      </section>
      <DrawerFooter className="flex w-full flex-row justify-end gap-2 p-6">
        <Button variant="contained" onClick={onClose}>
          {t('done')}
        </Button>
      </DrawerFooter>
    </Drawer>
  );
}
